/*Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Heading,
  Spinner,
  Warning
} from '../'

import {
  showAsCurrency
} from '../../utils'

class IdentityWellbeingStatement extends React.Component {
  static propTypes = {
    identity: PropTypes.string.isRequired,
    className: PropTypes.string
  }

  state = {
    refetch: false,
    selectedRows: []
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.error && !this.state.refetch) {
      this.setState({ refetch: true })
      nextProps.refetch()
    }

    if (this.state.refetch) {
      this.setState({ refetch: false })
    }
  }

  render() {
    if (this.props.data.loading) {
      return (
        <div className='slds-m-top_xx-large'>
          <Spinner className='slds-is-relative' />
        </div>
      )
    }

    if (this.props.data.error) {
      return (
        <Warning className='slds-m-around_x-large u-font-size-larger'>
          Error fetching identity data.
        </Warning>
      )
    }

    let {
      balanceShorttermInvestedIncl,
      balanceShorttermInProcess,
      balanceStWithdrawalProcessing,
      balanceStWithdrawalPending,
      balanceShorttermAvailable,
      balanceShorttermUnavailable,

      balanceEmergencyInvestedIncl,
      balanceEmergencyInProcess,
      balanceEsWithdrawalProcessing,
      balanceEsWithdrawalPending,

      balanceLongtermInvestedIncl,
      balanceLongtermInProcess,

      balanceDebtOutstanding,
      securityFrozenShortterm
    } = this.props.data.identity

    return (
      <div className={this.props.className ? this.props.className : ''}>
        <ShorttermInvestmentBalances
          balanceShorttermInvestedIncl={balanceShorttermInvestedIncl}
          balanceShorttermInProcess={balanceShorttermInProcess}
          balanceShorttermWithdrawalProcessing={balanceStWithdrawalProcessing}
          balanceShorttermWithdrawalPending={balanceStWithdrawalPending}
          balanceShorttermAvailable={balanceShorttermAvailable}
          balanceShorttermUnavailable={balanceShorttermUnavailable}
        />
        <EmergencyInvestmentBalances
          balanceEmergencyInvestedIncl={balanceEmergencyInvestedIncl}
          balanceEmergencyInProcess={balanceEmergencyInProcess}
          balanceEmergencyWithdrawalProcessing={balanceEsWithdrawalProcessing}
          balanceEmergencyWithdrawalPending={balanceEsWithdrawalPending}
        />
        <LongTermInvestmentBalances
          balanceLongtermInvestedIncl={balanceLongtermInvestedIncl}
          balanceLongtermInProcess={balanceLongtermInProcess}
        />
        <LoanBalances
          balanceDebtOutstanding={balanceDebtOutstanding}
          securityFrozenShortterm={securityFrozenShortterm}
        />
      </div>
    )
  }
}

const ShorttermInvestmentBalances = ({ balanceShorttermInvestedIncl, balanceShorttermInProcess, balanceShorttermWithdrawalProcessing, balanceShorttermWithdrawalPending, balanceShorttermAvailable, balanceShorttermUnavailable }) => (
  <div>
    <Heading marginBottom={15}>
      Short-Term Investment Balances
    </Heading>
    <Stat
      label='Investment Balance'
      value={balanceShorttermInvestedIncl}
      explanation='Total sum of invested contributions and interest in Finworks as Market Value'
    />
    <Stat
      label='Investment in Progress'
      value={balanceShorttermInProcess}
      explanation='Receipted + Exported + Uploaded + Paid Over'
    />
    <Stat
      label='Withdrawal Processing'
      value={balanceShorttermWithdrawalProcessing}
      explanation='Withdrawal requested but payout process not started yet'
    />
    <Stat
      label='Withdrawal Pending'
      value={balanceShorttermWithdrawalPending}
      explanation='Withdrawal requested but not approved yet'
    />
    <Stat
      label='Available'
      value={balanceShorttermAvailable}
      explanation='Investment Balance + Investment in Progress - Withdrawal Processing - Short-term Investment Loan Security'
    />
    <Stat
      label='Unavailable'
      value={balanceShorttermUnavailable}
      explanation='Withdrawal Processing + Short-term Investment Loan Security'
    />
  </div>
)

const EmergencyInvestmentBalances = ({ balanceEmergencyInvestedIncl, balanceEmergencyInProcess, balanceEmergencyWithdrawalProcessing, balanceEmergencyWithdrawalPending }) => (
  <div className='slds-m-top_x-large'>
    <Heading marginBottom={15}>
      Emergency Savings Balances
    </Heading>
    <Stat
      label='Investment Balance'
      value={balanceEmergencyInvestedIncl}
      explanation='Total sum of invested contributions and interest in Finworks as Market Value'
    />
    <Stat
      label='Investment in Progress'
      value={balanceEmergencyInProcess}
      explanation='Receipted + Exported + Uploaded + Paid Over'
    />
    <Stat
      label='Withdrawal Processing'
      value={balanceEmergencyWithdrawalProcessing}
      explanation='Withdrawal requested but payout process not started yet'
    />
    <Stat
      label='Withdrawal Pending'
      value={balanceEmergencyWithdrawalPending}
      explanation='Withdrawal requested but not approved yet'
    />
  </div>
)

const LongTermInvestmentBalances = ({ balanceLongtermInvestedIncl, balanceLongtermInProcess }) => (
  <div className='slds-m-top_x-large'>
    <Heading marginBottom={15}>
      Long-Term Investment Balances
    </Heading>
    <Stat
      label='Investment Balance'
      value={balanceLongtermInvestedIncl}
      explanation='Total sum of invested contributions and interest in Finworks as Market Value'
    />
    <Stat
      label='Investment in Progress'
      value={balanceLongtermInProcess}
      explanation='Receipted + Exported + Uploaded + Paid Over'
    />
  </div>
)

const LoanBalances = ({ balanceDebtOutstanding, securityFrozenShortterm }) => (
  <div className='slds-m-top_x-large'>
    <Heading marginBottom={15}>
      Loan Balances
    </Heading>
    <Stat
      label='Outstanding loans'
      value={balanceDebtOutstanding}
      explanation='Sum of outstanding balance for all active loans'
      sizeClassName='slds-size_7-of-12'
    />
    <Stat
      label='Short-term investment loan security'
      value={securityFrozenShortterm}
      explanation='Sum of Loans Outstanding - Long-term Investments (0 if negative)'
      sizeClassName='slds-size_7-of-12'
    />
  </div>
)

const Stat = ({ label, value, explanation, sizeClassName }) => (
  <React.Fragment>
    <div className={`slds-grid slds-wrap slds-m-top_x-small u-font-size-larger ${sizeClassName ? sizeClassName : 'slds-size_1-of-2'}`}>
      <div className='slds-size_1-of-2 u-font-weight-bold'>
        {`${label}:`}
      </div>
      <div className='slds-size_1-of-2'>
        {showAsCurrency('R', value, 2, true)}
      </div>
    </div>
    <div className='slds-text-body_small slds-text-color_weak'>
      {explanation}
    </div>
    <div className='slds-p-bottom_medium slds-border_bottom slds-size_1-of-12' />
  </React.Fragment>
)

const getIdentity = gql`
  query($id: ID!) {
    identity(id: $id) {
      id
      balanceShorttermInvestedIncl
      balanceShorttermInProcess
      balanceStWithdrawalProcessing
      balanceStWithdrawalPending
      balanceShorttermAvailable
      balanceShorttermUnavailable

      balanceEmergencyInvestedIncl
      balanceEmergencyInProcess
      balanceEsWithdrawalProcessing
      balanceEsWithdrawalPending

      balanceLongtermInvestedIncl
      balanceLongtermInProcess

      balanceDebtOutstanding
      securityFrozenShortterm
    }
  }
`

const withData = graphql(getIdentity, {
  options: (ownProps) => ({
    variables: {
      id: ownProps.identity
    }
  })
})

export default withData(IdentityWellbeingStatement)



// WEBPACK FOOTER //
// ./src/components/identity-wellbeing-statement/index.js